'use server';
/**
 * Owner-initiated publication ("предложить в галерею" on the story page) and the
 * owner side of an admin nomination: the story page shows an invite while
 * stories.public_status = 'nominated'; accepting records the SAME consent the
 * wizard records (version + timestamp) and moves the story to admin review,
 * declining returns it to 'none'. Scoped to the caller's family.
 */
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';
import { SessionService } from '@/contexts/authentication';
import type { ActionResult } from '@/shared/types/actions';
import { getPublishingRepository } from '../repositories/PublishingRepository';
import { TEXT_READY_STATUSES } from '../services/nomination';
import { PUBLIC_CONSENT_VERSION } from '../consent';

export async function respondToPublicationInvite(
  storyId: string,
  accept: boolean,
): Promise<ActionResult<{ publicStatus: 'pending' | 'none' }>> {
  const session = await SessionService.requireSession();
  const familyId = session.user?.familyId;
  if (!familyId) return { success: false, error: 'No family in session', code: 'AUTH' };
  try {
    const ok = await getPublishingRepository().respondToNomination(storyId, familyId, accept, PUBLIC_CONSENT_VERSION);
    if (!ok) return { success: false, error: 'Invite no longer active', code: 'STATE' };
    return { success: true, data: { publicStatus: accept ? 'pending' : 'none' } };
  } catch (e) {
    actionErrorLog('respondToPublicationInvite', e);
    return { success: false, error: e instanceof Error ? e.message : 'Failed' };
  }
}

/**
 * Owner asks for their story to be published: records consent (same version as
 * the wizard) and puts the story into admin review. Idempotent for stories
 * already in the pipeline; a rejected story is reported as 'pending' to the
 * owner (we do not tell authors about rejections — public-publishing-plan.md).
 */
export async function requestPublication(
  storyId: string,
): Promise<ActionResult<{ publicStatus: 'pending' | 'approved' | 'published' }>> {
  const session = await SessionService.requireSession();
  const familyId = session.user?.familyId;
  if (!familyId) return { success: false, error: 'No family in session', code: 'AUTH' };
  try {
    const repo = getPublishingRepository();
    const state = await repo.getPublicState(storyId);
    if (!state || state.familyId !== familyId) return { success: false, error: 'Story not found', code: 'NOT_FOUND' };
    if (!(TEXT_READY_STATUSES as readonly string[]).includes(state.status)) {
      return { success: false, error: 'Story is not finished yet', code: 'NOT_READY' };
    }
    switch (state.publicStatus) {
      case 'none':
      case 'nominated':
        await repo.recordConsent(storyId, PUBLIC_CONSENT_VERSION, 'pending');
        return { success: true, data: { publicStatus: 'pending' } };
      case 'published':
        return { success: true, data: { publicStatus: 'published' } };
      case 'approved':
      case 'publishing':
      case 'publish_failed':
        return { success: true, data: { publicStatus: 'approved' } };
      default: // pending | rejected
        return { success: true, data: { publicStatus: 'pending' } };
    }
  } catch (e) {
    actionErrorLog('requestPublication', e);
    return { success: false, error: e instanceof Error ? e.message : 'Failed' };
  }
}

/** Owner withdraws a request that is still waiting for review. */
export async function withdrawPublicationRequest(storyId: string): Promise<ActionResult<{ publicStatus: 'none' }>> {
  const session = await SessionService.requireSession();
  const familyId = session.user?.familyId;
  if (!familyId) return { success: false, error: 'No family in session', code: 'AUTH' };
  try {
    const ok = await getPublishingRepository().withdrawRequest(storyId, familyId);
    if (!ok) return { success: false, error: 'Request is no longer pending', code: 'STATE' };
    return { success: true, data: { publicStatus: 'none' } };
  } catch (e) {
    actionErrorLog('withdrawPublicationRequest', e);
    return { success: false, error: e instanceof Error ? e.message : 'Failed' };
  }
}
